import React from 'react';
import { 
  Box, 
  Typography, 
  LinearProgress, 
  Paper, 
  Collapse, 
  Alert, 
  AlertTitle, 
  Chip
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
// Removed unused import: HourglassEmptyIcon
import { PatchJobStatus, MenuJobStatus } from '../../../services/api';

interface JobStatusIndicatorProps {
  jobId?: string;
  status?: string;
  progress?: number;
  total?: number;
  error?: string | null;
  isSubmitting?: boolean;
  success?: boolean;
  type?: 'patch' | 'menu';
  totalSubJobs?: number;
  totalSubJobsCompleted?: number;
  totalSubJobsFailed?: number;
  totalSubJobsInProgress?: number;
  startedAt?: string;
  completedAt?: string;
}

/**
 * Component to display the status of a patch or menu job.
 * Shows a progress indicator, job status, and completion information.
 */
export const JobStatusIndicator: React.FC<JobStatusIndicatorProps> = ({
  jobId,
  status,
  progress = 0,
  total = 0,
  error,
  isSubmitting = false,
  success = false,
  type = 'patch',
  totalSubJobs,
  totalSubJobsCompleted,
  totalSubJobsFailed,
  totalSubJobsInProgress,
  startedAt,
  completedAt
}) => {
  // Calculate completion percentage
  const completionPercentage = totalSubJobs ? 
    Math.round((totalSubJobsCompleted || 0) / totalSubJobs * 100) : 
    (total > 0 ? Math.round((progress / total) * 100) : 0);
  
  // Determine the status color and icon
  const getStatusInfo = () => {
    if (error || totalSubJobsFailed) {
      return {
        color: 'error',
        icon: <ErrorOutlineIcon color="error" />,
        text: 'Failed',
      };
    }
    
    if (success || status === PatchJobStatus.COMPLETED || status === MenuJobStatus.COMPLETED) {
      return {
        color: 'success',
        icon: <CheckCircleOutlineIcon color="success" />,
        text: 'Completed',
      };
    }
    
    return {
      color: 'default',
      icon: null,
      text: 'Not Started',
    };
  };
  
  const statusInfo = getStatusInfo();
  
  // Move isInProgress into render scope
  const isInProgress = type === 'patch'
    ? status === PatchJobStatus.QUEUED || status === PatchJobStatus.STARTED || status === PatchJobStatus.IN_PROGRESS
    : status === MenuJobStatus.QUEUED || status === MenuJobStatus.STARTED || status === MenuJobStatus.IN_PROGRESS;

  return (
    <Collapse in={!!(isSubmitting || jobId || error || success || status)}>
      <Paper elevation={2} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Box sx={{ width: '100%' }}>
          {/* Title and status */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6" component="div">
              {type === 'patch' ? 'Patch Job Status' : 'Menu Job Status'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {statusInfo.icon}
              <Chip 
                label={statusInfo.text}
                color={statusInfo.color as "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning"}
                size="small"
                sx={{ ml: 1 }}
              />
            </Box>
          </Box>
          
          {/* Progress bar and progress text only when in progress */}
          {(isSubmitting || isInProgress) && !(
            status === PatchJobStatus.COMPLETED || status === MenuJobStatus.COMPLETED ||
            status === PatchJobStatus.FAILED || status === MenuJobStatus.FAILED
          ) && (
            <Box sx={{ mt: 2, mb: 1 }}>
              <LinearProgress 
                variant={completionPercentage > 0 ? "determinate" : "indeterminate"} 
                value={completionPercentage} 
                sx={{ height: 8, borderRadius: 2 }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                  Processing {type === 'patch' ? 'sub-jobs' : 'stores'}...
                </Typography>
                { (totalSubJobs && totalSubJobs > 0) ? (
                  <Typography variant="body2" color="text.secondary">
                    {`${totalSubJobsCompleted || 0} of ${totalSubJobs} completed`} {completionPercentage ? `(${completionPercentage}%)` : null}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {total ? `${progress} of ${total} completed` : ''} {completionPercentage ? `(${completionPercentage}%)` : ''}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
          
          {/* Show completion summary */}
          {(status === PatchJobStatus.COMPLETED || status === MenuJobStatus.COMPLETED) && (
            <Alert severity="success" sx={{ mt: 1 }}>
              <AlertTitle>Success</AlertTitle>
              All {type} operations completed successfully. {totalSubJobs || total} {type === 'patch' ? 'sub-jobs' : 'stores'} processed.
              {completedAt && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Completed at: {new Date(completedAt).toLocaleString()}
                </Typography>
              )}
            </Alert>
          )}
          
          {/* Show error if there is one */}
          {(error || !!totalSubJobsFailed) && (
            <Alert severity="error" sx={{ mt: 1 }}>
              <AlertTitle>Error</AlertTitle>
              {error || `${totalSubJobsFailed} ${type === 'patch' ? 'sub-jobs' : 'stores'} failed`}
            </Alert>
          )}
        </Box>
      </Paper>
    </Collapse>
  );
};

export default JobStatusIndicator;